import { baseApi } from '@shared/api/baseApi';
import type { ApiMessage } from '@shared/api/response';
import type {
  ChatMessage,
  ChatPartner,
  ChatThread,
  NotificationCategory,
  NotificationPage,
  QueryClientRow,
  QueryClientView,
  RaiseQueriesInput,
  SavedChatMessage,
  SubQueryDetail,
  SubQueryList,
  TicketView,
} from '@shared/api/types';
import { type DownloadedFile } from '@shared/lib/download';

export type ReadFilter = 'all' | 'unread' | 'read' | 'archived';
export type Period = 'all' | 'today' | 'week' | 'month' | 'custom';
export type QueryStatusFilter = 'all' | 'open' | 'resolved';

export interface NotificationQuery {
  category: NotificationCategory;
  read: ReadFilter;
  period: Period;
  from?: string | undefined;
  to?: string | undefined;
  /** Client name or code. */
  q?: string | undefined;
  page: number;
  pageSize: number;
}

export interface QueryScope {
  year: number;
  raisedBy?: string | undefined;
  status: QueryStatusFilter;
}

function multipart(text: string, files: File[]): FormData {
  const form = new FormData();
  form.append('text', text);
  for (const f of files) form.append('files', f);
  return form;
}

/** Everything a query change can move: lists, the badge, boards and task drawers. */
const QUERY_CHANGED = ['Query', 'Task', 'InboxSummary', 'Notification'] as const;

const inboxApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // ---- notifications
    notifications: build.query<NotificationPage, NotificationQuery>({
      query: (params) => ({ url: '/notifications', params }),
      providesTags: ['Notification'],
    }),
    markNotification: build.mutation<ApiMessage, { id: string; read: boolean }>({
      query: ({ id, read }) => ({ url: `/notifications/${id}/${read ? 'read' : 'unread'}`, method: 'POST' }),
      invalidatesTags: ['Notification', 'InboxSummary'],
    }),
    archiveNotification: build.mutation<ApiMessage, { id: string; archived: boolean }>({
      query: ({ id, archived }) => ({
        url: `/notifications/${id}/${archived ? 'archive' : 'unarchive'}`,
        method: 'POST',
      }),
      invalidatesTags: ['Notification', 'InboxSummary'],
    }),
    markAllNotifications: build.mutation<ApiMessage, undefined>({
      query: () => ({ url: '/notifications/read-all', method: 'POST' }),
      invalidatesTags: ['Notification', 'InboxSummary'],
    }),

    // ---- chat
    chatPartners: build.query<ChatPartner[], string | undefined>({
      query: (q) => ({ url: '/chat/partners', params: q ? { q } : {} }),
      providesTags: ['ChatPartners'],
    }),
    chatThread: build.query<ChatThread, string>({
      query: (partnerId) => `/chat/threads/${partnerId}`,
      providesTags: (_r, _e, id) => [{ type: 'ChatThread', id }],
    }),
    sendChat: build.mutation<ApiMessage<ChatMessage>, { partnerId: string; text: string; files: File[] }>({
      query: ({ partnerId, text, files }) => ({
        url: `/chat/threads/${partnerId}/messages`,
        method: 'POST',
        body: multipart(text, files),
      }),
      invalidatesTags: (_r, _e, { partnerId }) => [{ type: 'ChatThread', id: partnerId }, 'ChatPartners'],
    }),
    readChat: build.mutation<ApiMessage, string>({
      query: (partnerId) => ({ url: `/chat/threads/${partnerId}/read`, method: 'POST' }),
      invalidatesTags: ['ChatPartners', 'InboxSummary'],
    }),
    setChatState: build.mutation<
      ApiMessage,
      { partnerId: string; pinned?: boolean | undefined; forceUnread?: boolean | undefined }
    >({
      query: ({ partnerId, ...body }) => ({ url: `/chat/threads/${partnerId}`, method: 'PATCH', body }),
      invalidatesTags: ['ChatPartners', 'InboxSummary'],
    }),
    deleteChat: build.mutation<ApiMessage, string>({
      query: (partnerId) => ({ url: `/chat/threads/${partnerId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ChatThread', id },
        'ChatPartners',
        'ChatSaved',
        'InboxSummary',
      ],
    }),
    savedChat: build.query<SavedChatMessage[], undefined>({
      query: () => '/chat/saved',
      providesTags: ['ChatSaved'],
    }),
    editChatMessage: build.mutation<ApiMessage, { messageId: string; partnerId: string; text: string }>({
      query: ({ messageId, text }) => ({
        url: `/chat/messages/${messageId}`,
        method: 'PATCH',
        body: { text },
      }),
      invalidatesTags: (_r, _e, { partnerId }) => [
        { type: 'ChatThread', id: partnerId },
        'ChatPartners',
        'ChatSaved',
      ],
    }),
    saveChatMessage: build.mutation<ApiMessage, { messageId: string; partnerId: string; saved: boolean }>({
      query: ({ messageId, saved }) => ({
        url: `/chat/messages/${messageId}/save`,
        method: saved ? 'POST' : 'DELETE',
      }),
      invalidatesTags: (_r, _e, { partnerId }) => ['ChatSaved', { type: 'ChatThread', id: partnerId }],
    }),

    // ---- queries
    queryClients: build.query<QueryClientRow[], QueryScope & { clientId?: string | undefined }>({
      query: (params) => ({ url: '/queries/clients', params }),
      providesTags: ['Query'],
    }),
    queryClient: build.query<QueryClientView, QueryScope & { clientId: string }>({
      query: ({ clientId, ...params }) => ({ url: `/queries/clients/${clientId}`, params }),
      providesTags: ['Query'],
    }),
    queryItems: build.query<SubQueryList, QueryScope & { clientId: string; ticketId?: string | undefined }>({
      query: (params) => ({ url: '/queries/items', params }),
      providesTags: ['Query'],
    }),
    subQuery: build.query<SubQueryDetail, string>({
      query: (id) => `/queries/sub-queries/${id}`,
      providesTags: ['Query'],
    }),
    ticketForTask: build.query<TicketView, string>({
      query: (obligationId) => `/queries/for-task/${obligationId}`,
      providesTags: ['Query'],
    }),
    raiseQueries: build.mutation<ApiMessage, RaiseQueriesInput>({
      query: (body) => ({ url: '/queries', method: 'POST', body }),
      invalidatesTags: [...QUERY_CHANGED],
    }),
    editQuery: build.mutation<ApiMessage, { id: string; text: string }>({
      query: ({ id, text }) => ({ url: `/queries/sub-queries/${id}`, method: 'PATCH', body: { text } }),
      invalidatesTags: [...QUERY_CHANGED],
    }),
    deleteQuery: build.mutation<ApiMessage, string>({
      query: (id) => ({ url: `/queries/sub-queries/${id}`, method: 'DELETE' }),
      invalidatesTags: [...QUERY_CHANGED],
    }),
    replyQuery: build.mutation<ApiMessage<SubQueryDetail>, { id: string; text: string; files: File[] }>({
      query: ({ id, text, files }) => ({
        url: `/queries/sub-queries/${id}/remarks`,
        method: 'POST',
        body: multipart(text, files),
      }),
      invalidatesTags: [...QUERY_CHANGED],
    }),
    resolveQuery: build.mutation<ApiMessage, { id: string; resolve: boolean }>({
      query: ({ id, resolve }) => ({
        url: `/queries/sub-queries/${id}/${resolve ? 'resolve' : 'reopen'}`,
        method: 'POST',
      }),
      invalidatesTags: [...QUERY_CHANGED],
    }),

    // ---- files (chat messages and query replies share the attachments endpoint)
    downloadInboxFile: build.mutation<DownloadedFile, { id: string; fileName: string }>({
      query: ({ id }) => ({ url: `/attachments/${id}`, responseHandler: (r) => r.blob(), cache: 'no-cache' }),
      transformResponse: (blob: Blob, _meta, { fileName }) => ({ blob, fileName }),
    }),
  }),
});

export const {
  useNotificationsQuery,
  useMarkNotificationMutation,
  useArchiveNotificationMutation,
  useMarkAllNotificationsMutation,
  useChatPartnersQuery,
  useChatThreadQuery,
  useSendChatMutation,
  useReadChatMutation,
  useSetChatStateMutation,
  useDeleteChatMutation,
  useSavedChatQuery,
  useEditChatMessageMutation,
  useSaveChatMessageMutation,
  useQueryClientsQuery,
  useQueryClientQuery,
  useQueryItemsQuery,
  useSubQueryQuery,
  useTicketForTaskQuery,
  useRaiseQueriesMutation,
  useEditQueryMutation,
  useDeleteQueryMutation,
  useReplyQueryMutation,
  useResolveQueryMutation,
  useDownloadInboxFileMutation,
} = inboxApi;
